import { useId, useState } from 'react';
import { BookmarkIcon, CopyIcon, EllipsisIcon, LockIcon, PencilIcon, UsersIcon, XIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BCE_SEED_V3 } from '@docgen/seed-bce';
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  TextArea,
  TextInput,
  type DataTableCard,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import {
  PAGE_LIBRARY_DESCRIPTION_MAX,
  PAGE_LIBRARY_NAME_MAX,
  PAGE_LIBRARY_QUERY_KEY,
  PAGE_LIBRARY_SCOPE,
  PAGE_LIBRARY_SORT_FIELD,
  PAGE_LIBRARY_VISIBILITY,
  copyLibraryItem,
  deleteLibraryItem,
  updateLibraryItem,
  useLibraryItems,
  type LibraryItemSummary,
  type PageLibraryScope,
  type PageLibrarySortField,
} from '@/proposals/lib/page-library';
import { previewDocument } from '@/proposals/lib/library-pages';
import { formatShortDate } from '@/proposals/lib/money';
import { PageThumbnail } from '@/proposals/library/PageThumbnail';

/** Sortable columns → the API's sort fields (the list is sorted and paged server-side). */
const LIBRARY_SORT_FIELDS = {
  name: PAGE_LIBRARY_SORT_FIELD.name,
  lastUsed: PAGE_LIBRARY_SORT_FIELD.lastUsedAt,
  uses: PAGE_LIBRARY_SORT_FIELD.usageCount,
} as const;
const LIBRARY_SORT_KEYS: readonly string[] = Object.values(LIBRARY_SORT_FIELDS);
const DEFAULT_LIBRARY_SORT: DataTableSort = { key: LIBRARY_SORT_FIELDS.name, direction: 'asc' };

function isSortField(key: string): key is PageLibrarySortField {
  return LIBRARY_SORT_KEYS.includes(key);
}

/** The list's filter, kept in the URL (`?scope=`). */
const SCOPE_PARAM = 'scope';
const SCOPE_OPTIONS = [
  { key: PAGE_LIBRARY_SCOPE.all, label: 'All' },
  { key: PAGE_LIBRARY_SCOPE.mine, label: 'Mine' },
  { key: PAGE_LIBRARY_SCOPE.team, label: 'Team' },
] as const;

const VISIBILITY_LABEL = {
  [PAGE_LIBRARY_VISIBILITY.private]: 'Private',
  [PAGE_LIBRARY_VISIBILITY.team]: 'Team',
} as const;

/** Row thumbnail width; the table row stays two text lines tall beside it. */
const ROW_THUMB_WIDTH_PX = 40;

function isScope(value: string | null): value is PageLibraryScope {
  return value === PAGE_LIBRARY_SCOPE.all || value === PAGE_LIBRARY_SCOPE.mine || value === PAGE_LIBRARY_SCOPE.team;
}

function visibilityLabel(item: LibraryItemSummary): string {
  return VISIBILITY_LABEL[item.visibility];
}

function lastUsedText(item: LibraryItemSummary): string {
  return item.lastUsedAt === null ? 'Not used yet' : formatShortDate(item.lastUsedAt);
}

/** The first page, drawn small against the BCE template's sample data (no proposal is open here). */
function RowThumbnail({ item }: { readonly item: LibraryItemSummary }): React.ReactElement {
  if (item.firstPage === null) {
    return <div aria-hidden className="h-[52px] w-[40px] shrink-0 rounded-[4px] border border-portal-line bg-white" />;
  }
  return <PageThumbnail doc={previewDocument(BCE_SEED_V3, [item.firstPage])} width={ROW_THUMB_WIDTH_PX} />;
}

/** The row's actions: everyone can save a copy; the owner and admins (the API's `canEdit`) can also rename, share and delete. */
function RowActions({
  item,
  canManage,
  onRename,
  onToggleShare,
  onCopy,
  onDelete,
}: {
  readonly item: LibraryItemSummary;
  readonly canManage: boolean;
  readonly onRename: () => void;
  readonly onToggleShare: () => void;
  readonly onCopy: () => void;
  readonly onDelete: () => void;
}): React.ReactElement {
  const shared = item.visibility === PAGE_LIBRARY_VISIBILITY.team;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Actions for ${item.name}`}
          className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
        >
          <EllipsisIcon aria-hidden className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canManage ? (
          <>
            <DropdownMenuItem onSelect={onRename}>
              <PencilIcon aria-hidden className="size-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onToggleShare}>
              {shared ? <LockIcon aria-hidden className="size-4" /> : <UsersIcon aria-hidden className="size-4" />}
              {shared ? 'Make private' : 'Share with the team'}
            </DropdownMenuItem>
          </>
        ) : null}
        <DropdownMenuItem onSelect={onCopy}>
          <CopyIcon aria-hidden className="size-4" />
          Save a copy
        </DropdownMenuItem>
        {canManage ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={onDelete}>
              <XIcon aria-hidden className="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Rename: the name and description people search by. Mounted only while open. */
function RenameDialog({
  item,
  onClose,
}: {
  readonly item: LibraryItemSummary;
  readonly onClose: () => void;
}): React.ReactElement {
  const nameId = useId();
  const descriptionId = useId();
  const queryClient = useQueryClient();
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description);
  const save = useMutation({
    mutationFn: () => updateLibraryItem(item.id, { name: name.trim(), description: description.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PAGE_LIBRARY_QUERY_KEY });
      onClose();
    },
  });
  const blank = name.trim() === '';
  return (
    <Modal
      open
      title="Rename"
      onClose={onClose}
      dismissible={!save.isPending}
      className="max-w-lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || blank}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!blank) save.mutate();
        }}
      >
        {save.isError ? <ErrorNotice inline title="Couldn't rename it." message={errorMessage(save.error)} /> : null}
        <Field label="Name" htmlFor={nameId} required>
          <TextInput
            id={nameId}
            value={name}
            maxLength={PAGE_LIBRARY_NAME_MAX}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Description" htmlFor={descriptionId} hint="People find pages by searching names and descriptions.">
          <TextArea
            id={descriptionId}
            rows={3}
            value={description}
            maxLength={PAGE_LIBRARY_DESCRIPTION_MAX}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/**
 * Proposals → Page library: every saved page set the caller can see (theirs,
 * and the team's shared ones). Pages are added to a proposal from its Pages
 * panel; this screen is for finding, renaming, sharing, copying and tidying.
 *
 * @returns The page library list.
 */
export function PageLibraryPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const scopeParam = params.get(SCOPE_PARAM);
  const scope: PageLibraryScope = isScope(scopeParam) ? scopeParam : PAGE_LIBRARY_SCOPE.all;
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(LIBRARY_SORT_KEYS, DEFAULT_LIBRARY_SORT);
  const [renaming, setRenaming] = useState<LibraryItemSummary | null>(null);
  const [deleting, setDeleting] = useState<LibraryItemSummary | null>(null);

  const items = useLibraryItems({
    scope,
    q: debounced,
    page,
    ...(sort && isSortField(sort.key) ? { sort: { field: sort.key, direction: sort.direction } } : {}),
  });

  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: PAGE_LIBRARY_QUERY_KEY });

  const share = useMutation({
    mutationFn: (item: LibraryItemSummary) =>
      updateLibraryItem(item.id, {
        visibility:
          item.visibility === PAGE_LIBRARY_VISIBILITY.team ? PAGE_LIBRARY_VISIBILITY.private : PAGE_LIBRARY_VISIBILITY.team,
      }),
    onSuccess: async (saved) => {
      toast.success(
        saved.visibility === PAGE_LIBRARY_VISIBILITY.team ? `${saved.name} is shared with the team` : `${saved.name} is private`,
      );
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const copy = useMutation({
    mutationFn: (item: LibraryItemSummary) => copyLibraryItem(item.id),
    onSuccess: async (saved) => {
      toast.success(`Saved a copy: ${saved.name}`);
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: (item: LibraryItemSummary) => deleteLibraryItem(item.id),
    onSuccess: async () => {
      toast.success(`Deleted ${deleting?.name ?? 'the pages'}`);
      setDeleting(null);
      await refresh();
    },
  });

  const setScope = (next: PageLibraryScope): void =>
    setParams((previous) => {
      const nextParams = new URLSearchParams(previous);
      if (next === PAGE_LIBRARY_SCOPE.all) nextParams.delete(SCOPE_PARAM);
      else nextParams.set(SCOPE_PARAM, next);
      nextParams.delete('page');
      return nextParams;
    });

  const rows = items.data?.items ?? [];

  const actionsFor = (item: LibraryItemSummary): React.ReactElement => (
    <RowActions
      item={item}
      canManage={item.canEdit}
      onRename={() => setRenaming(item)}
      onToggleShare={() => share.mutate(item)}
      onCopy={() => copy.mutate(item)}
      onDelete={() => setDeleting(item)}
    />
  );

  const cards: DataTableCard[] = rows.map((item) => ({
    key: item.id,
    title: item.name,
    ...(item.description ? { subtitle: item.description } : {}),
    fields: [
      { label: 'Pages', value: item.pageCount },
      { label: 'Owner', value: item.ownerName || '—' },
      { label: 'Visibility', value: <Pill tone={item.visibility === PAGE_LIBRARY_VISIBILITY.team ? 'brand' : 'neutral'}>{visibilityLabel(item)}</Pill> },
      { label: 'Last used', value: lastUsedText(item) },
      { label: 'Uses', value: item.usageCount },
    ],
    action: actionsFor(item),
  }));

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        title="Page library"
        subtitle="Pages saved from proposals, yours and the ones shared with the team. Add them to a proposal from its Pages panel."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search the page library"
          placeholder="Name, description or page text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <SegmentedControl label="Whose pages to show" options={SCOPE_OPTIONS} value={scope} onChange={setScope} />
        {items.data === undefined ? null : (
          <span className="text-portal-micro text-portal-muted">
            {items.data.pagination.total} {items.data.pagination.total === 1 ? 'item' : 'items'}
          </span>
        )}
      </div>

      {items.isError ? (
        <ErrorNotice title="Page library unavailable" message={errorMessage(items.error)} />
      ) : items.isPending ? (
        <SkeletonTable label="Loading the page library…" />
      ) : rows.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState
              message="No saved pages match that search."
              action={
                <Button variant="secondary" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<BookmarkIcon aria-hidden className="size-6" />}
              title={scope === PAGE_LIBRARY_SCOPE.team ? 'No team pages yet' : 'No saved pages yet'}
              message="In a proposal, pick pages in the Pages panel and choose Save to library. They show up here, ready for every proposal."
              action={<Button to="/proposals">Open a proposal and save a page</Button>}
            />
          )}
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Name', sortKey: LIBRARY_SORT_FIELDS.name },
            // Pages, Owner and Visibility have no server-side order, and the list is paged.
            'Pages',
            'Owner',
            'Visibility',
            { label: 'Last used', sortKey: LIBRARY_SORT_FIELDS.lastUsed },
            { label: 'Uses', sortKey: LIBRARY_SORT_FIELDS.uses },
            'Actions',
          ]}
          visuallyHiddenHeaders={[6]}
          rightAlignedHeaders={[1, 5]}
          sort={sort}
          onSort={onSort}
          minWidth={880}
          cards={cards}
        >
          {rows.map((item) => (
            <tr key={item.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <span className="flex items-center gap-3">
                  <RowThumbnail item={item} />
                  <span className="min-w-0">
                    <b className="block text-portal-small">{item.name}</b>
                    {item.description ? (
                      <span className="block max-w-[420px] truncate text-portal-micro text-portal-muted">{item.description}</span>
                    ) : null}
                  </span>
                </span>
              </td>
              <td className="p-3 text-right text-portal-small whitespace-nowrap">{item.pageCount}</td>
              <td className="p-3 text-portal-small text-portal-muted">{item.ownerName || '—'}</td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={item.visibility === PAGE_LIBRARY_VISIBILITY.team ? 'brand' : 'neutral'}>{visibilityLabel(item)}</Pill>
              </td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{lastUsedText(item)}</td>
              <td className="p-3 text-right text-portal-small whitespace-nowrap">{item.usageCount}</td>
              <td className="p-3 text-right">{actionsFor(item)}</td>
            </tr>
          ))}
        </DataTable>
      )}
      {items.data && items.data.pagination.totalPages > 1 ? (
        <Pagination page={page} totalPages={items.data.pagination.totalPages} onChange={setPage} />
      ) : null}

      {renaming ? <RenameDialog item={renaming} onClose={() => setRenaming(null)} /> : null}

      <ConfirmDialog
        open={deleting !== null}
        tone="danger"
        title={`Delete ${deleting?.name ?? 'these pages'}?`}
        message="It leaves the library for everyone. Proposals that already use these pages keep them as they are."
        confirmLabel="Delete"
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
        onCancel={() => {
          if (!remove.isPending) setDeleting(null);
        }}
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
      />
    </section>
  );
}
